/**
 * 聚合 generate 节点的"上游输入"：
 *  - 上游文本节点的 content 拼成最终 prompt
 *  - 上游图片节点的 imageUrl 作为 referenceImages（图片组逐张）
 *  - 上游视频节点不参与（画布规则：任何节点不得消费视频，例外见 aggregateUpstreamVideoUrls）
 *
 * 聚合规则：
 *  - 多个上游文本按节点 createdAt 顺序拼接（同一画布 createdAt 不可比较时，按 zIndex 再按 id 字典序）
 *  - generate 节点自身的 data.prompt 视为"末尾追加内容"
 */
import type {
  CanvasEdge,
  CanvasNode,
  AudioNodeData,
  GenerateImageNodeData,
  GenerateVideoNodeData,
  ImageGroupNodeData,
  ImageNodeData,
  LlmNodeData,
  StyleCardNodeData,
  TextNodeData,
  VideoGroupNodeData,
  VideoNodeData,
} from '../types';
import { getBoardState } from './boardStore';
import { htmlToPlainText } from '../utils/richTextBox';

export interface AggregatedUpstream {
  prompt: string;
  referenceImages: string[];
  /** first_last_frame 模式专用，按上游连接顺序：第一张作为首帧，第二张作为尾帧 */
  firstFrameImage?: string;
  lastFrameImage?: string;
}

/**
 * 上游输入签名：供 useBoard selector 订阅。
 * 返回值是字符串，上游内容不变时 Object.is 相等，组件不重渲染；
 * 避免生成/LLM 节点订阅全量 nodes/edges 导致任何画布变动都重渲染（memo 被击穿）。
 */
export function buildUpstreamSignature(nodeId: string, nodes: CanvasNode[], edges: CanvasEdge[]): string {
  const sourceIds = edges
    .filter((e) => e.targetNodeId === nodeId)
    .map((e) => e.sourceNodeId)
    .sort();
  return sourceIds
    .map((id) => {
      const n = nodes.find((x) => x.id === id);
      if (!n) return `${id}:missing`;
      const d = n.data as Record<string, unknown>;
      const text = String(d.content ?? d.output ?? d.prompt ?? '');
      const media = String(d.imageUrl ?? d.videoUrl ?? d.audioStorageKey ?? '');
      const group = Array.isArray(d.imageUrls) ? (d.imageUrls as string[]).join(',') : '';
      // 视频组的内容与顺序变化同样要触发下游重算
      const videoGroup = Array.isArray(d.videoUrls) ? (d.videoUrls as string[]).join(',') : '';
      // LLM 图片透传：开关状态与展开后的深层图片资产都计入签名，
      // 开关切换或深层图片变化时下游必须重新聚合
      const passthrough = n.type === 'llm' && d.passthroughImages ? '1' : '0';
      const passthroughAssets =
        passthrough === '1'
          ? collectUpstreamImageAssetGroups(n.id, nodes, edges)
              .map((g) => `${g.refNo ?? ''}@${g.urls.join(',')}`)
              .join(';')
          : '';
      return `${id}:${n.type}:${text}:${media}:${group}:${videoGroup}:${passthrough}:${passthroughAssets}`;
    })
    .join('|');
}

/**
 * 将完整 API URL 归一化为相对路径。
 * 后端轮询接口返回的 resultUrl 带 host（如 http://xxx/api/images/file/...），
 * 直接发给后端 saveReferenceImagesAsFiles 会被转成绝对路径导致适配器无法识别。
 * 去掉 host 后变为 /api/images/file/...，后端能正确处理为相对路径。
 *
 * 注意：blob: URL（本地上传的图片）原样保留，由生成器在提交 API 前统一
 * 转为 data URL（见 imageStorage.resolveBlobUrlsForApi），不再静默丢弃。
 */
export function normalizeImageUrlForApi(url: string): string {
  // blob URL 保留原样，交给生成器解析为 data URL
  if (url.startsWith('blob:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
  return url;
}

function compareNodesForOrder(a: CanvasNode, b: CanvasNode): number {
  if (a.zIndex !== b.zIndex) return a.zIndex - b.zIndex;
  return a.id.localeCompare(b.id);
}

/** 上游排序统一口径：目标节点 data.inputOrder（手动排序）优先，否则 zIndex + id */
function sortUpstreamByOrder(upstream: CanvasNode[], inputOrder: string[] | undefined): CanvasNode[] {
  if (inputOrder && inputOrder.length > 0) {
    const orderMap = new Map(inputOrder.map((id, idx) => [id, idx]));
    return [...upstream].sort((a, b) => {
      const oa = orderMap.get(a.id) ?? 9999;
      const ob = orderMap.get(b.id) ?? 9999;
      return oa - ob;
    });
  }
  return [...upstream].sort(compareNodesForOrder);
}

/**
 * 聚合指定节点的上游参考图（image 1 张 / image_group 逐张）。
 * 与 aggregateUpstream 的参考图口径一致，但不要求节点是 generate 类型——
 * 智能体节点等编排型节点也可复用同一收集逻辑。
 * 视频不参与（画布规则：视频仅可作为生视频节点的参考视频，见 aggregateUpstreamVideoUrls）。
 */
export function aggregateUpstreamReferenceImages(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): string[] {
  // 统一走分组收集：既覆盖 image / image_group，也会展开开启透传的上游 LLM
  const referenceImages: string[] = [];
  for (const group of collectUpstreamImageAssetGroups(targetNodeId, nodes, edges)) {
    for (const url of group.urls) referenceImages.push(url);
  }
  return referenceImages;
}

/**
 * 聚合指定节点的上游文本（text + llm）。
 * LLM 节点使用：将上游文本拼接后作为 prompt 的前缀部分。
 */
export function aggregateUpstreamText(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): string {
  const upstreamIds = edges
    .filter((e) => e.targetNodeId === targetNodeId)
    .map((e) => e.sourceNodeId);
  const upstream = nodes.filter((n) => upstreamIds.includes(n.id));
  upstream.sort(compareNodesForOrder);

  const textParts: string[] = [];
  for (const node of upstream) {
    if (node.type === 'text') {
      // 文本节点内容可能是富文本 HTML（行内/放大编辑），拼接前转纯文本
      const content = (node.data as TextNodeData).content;
      const plain = content ? htmlToPlainText(content) : '';
      if (plain && plain.trim()) textParts.push(plain.trim());
    } else if (node.type === 'llm') {
      const output = (node.data as LlmNodeData).output;
      if (output && output.trim()) textParts.push(output.trim());
    } else if (node.type === 'style_card') {
      const prompt = (node.data as StyleCardNodeData).prompt;
      if (prompt && prompt.trim()) textParts.push(prompt.trim());
    }
  }
  return textParts.join('\n\n');
}

/**
 * 聚合指定节点的上游参考视频（video 节点的 videoUrl，按连线顺序）。
 * 画布视频消费的唯一例外：仅"当前模型+模式支持参考视频"的生视频节点消费
 * （能力判定与连线拦截见 connectionRules.canConnectNodes / modelCache）。
 * 只收服务端可解析的引用（媒体网关路径 / 公网 URL）；blob:、data: 无法作为
 * 参考视频提交（后端仅支持公网 URL / 网关 URL / storage key），直接过滤。
 */
export function aggregateUpstreamVideoUrls(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): string[] {
  return aggregateUpstreamVideoRefs(targetNodeId, nodes, edges).map((ref) => ref.url);
}

/**
 * 聚合上游参考视频（含时长）：durationSec 来自视频结果节点的 videoDurationSec
 * （任务完成时由 job.resultDuration 回写）；历史节点无时长按 0 处理（输入侧计费按未知时长不计）。
 * 下标与 aggregateUpstreamVideoUrls 输出的 URL 一一对应，随生成请求提交给后端计费。
 */
export function aggregateUpstreamVideoRefs(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): Array<{ url: string; durationSec: number }> {
  const upstreamIds = edges
    .filter((e) => e.targetNodeId === targetNodeId)
    .map((e) => e.sourceNodeId);
  const upstream = nodes
    .filter((n) => upstreamIds.includes(n.id))
    .sort(compareNodesForOrder);

  const refs: Array<{ url: string; durationSec: number }> = [];
  for (const node of upstream) {
    // 视频组：整组视频按组内顺序展开为参考视频
    if (node.type === 'video_group') {
      const gd = node.data as VideoGroupNodeData;
      const urls = gd.videoUrls ?? [];
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        if (!url || url.startsWith('blob:') || url.startsWith('data:')) continue;
        const durationSec = Number((gd.durations ?? [])[i]);
        refs.push({
          url,
          durationSec: Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0,
        });
      }
      continue;
    }
    if (node.type !== 'video') continue;
    const data = node.data as VideoNodeData;
    const url = data.videoUrl;
    if (!url) continue;
    if (url.startsWith('blob:') || url.startsWith('data:')) continue;
    const durationSec = Number(data.videoDurationSec);
    refs.push({
      url,
      durationSec: Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0,
    });
  }
  return refs;
}

/**
 * 聚合指定节点的上游参考音频（audio 节点的 audioStorageKey，按连线顺序）。
 * 只收 storage key（后端 resolveAudioReferenceForUpstream 能解析 videos/ 前缀的 key）。
 */
export function aggregateUpstreamAudioReferences(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): string[] {
  const upstreamIds = edges
    .filter((e) => e.targetNodeId === targetNodeId)
    .map((e) => e.sourceNodeId);
  const upstream = nodes
    .filter((n) => upstreamIds.includes(n.id))
    .sort(compareNodesForOrder);

  const refs: string[] = [];
  for (const node of upstream) {
    if (node.type !== 'audio') continue;
    const data = node.data as AudioNodeData;
    const key = data.audioStorageKey;
    if (key) refs.push(key);
  }
  return refs;
}

export function aggregateUpstream(
  generateNode: CanvasNode<GenerateImageNodeData | GenerateVideoNodeData>,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): AggregatedUpstream {
  const upstreamIds = edges
    .filter((e) => e.targetNodeId === generateNode.id)
    .map((e) => e.sourceNodeId);
  let upstream = nodes.filter((n) => upstreamIds.includes(n.id));

  // 如果 generate 节点有 inputOrder，按 inputOrder 排序；否则按 zIndex + id
  const inputOrder = (generateNode.data as Record<string, unknown>).inputOrder as string[] | undefined;
  upstream = sortUpstreamByOrder(upstream, inputOrder);

  const textParts: string[] = [];
  for (const node of upstream) {
    if (node.type === 'text') {
      // 富文本 HTML 转纯文本后再拼接（与 aggregateUpstreamText 同口径）
      const content = (node.data as TextNodeData).content;
      const plain = content ? htmlToPlainText(content) : '';
      if (plain && plain.trim()) textParts.push(plain.trim());
    } else if (node.type === 'llm') {
      const output = (node.data as LlmNodeData).output;
      if (output && output.trim()) textParts.push(output.trim());
    } else if (node.type === 'style_card') {
      const prompt = (node.data as StyleCardNodeData).prompt;
      if (prompt && prompt.trim()) textParts.push(prompt.trim());
    }
  }

  // 参考图：图片节点 / 图片组逐张，并展开开启「图片透传」的上游 LLM 节点
  //（统一走分组收集，保证与 @引用 / 节点置灰同一口径）
  const referenceImages = aggregateUpstreamReferenceImages(generateNode.id, nodes, edges);

  // generate 节点自身的 prompt 追加在最后
  const ownPrompt = (generateNode.data.prompt || '').trim();
  if (ownPrompt) textParts.push(ownPrompt);

  return {
    prompt: textParts.join('\n\n'),
    referenceImages,
    firstFrameImage: referenceImages[0],
    lastFrameImage: referenceImages[1],
  };
}

export interface UpstreamImageAssetGroup {
  nodeId: string;
  type: CanvasNode['type'];
  /** 稳定图号（@图N 的 N 按它定位）；旧数据缺失时为 undefined，不可被 @ 引用 */
  refNo?: number;
  /** 该上游节点的图片资产（image 1 张 / image_group 逐张），其余类型为空数组 */
  urls: string[];
}

/**
 * 按上游节点分组收集图片资产：@图N 引用按**稳定图号**定位上游
 * （图号刻在节点上、终身不变，与连线位置无关——删除/增连其他节点不漂移）。
 * 只保留有图片资产的上游（image 有图 / image_group 有任一图），按连线顺序排列。
 * 开启「图片透传」的 llm 上游会被展开为其自身的图片资产（保留原稳定图号）；
 * 跨层按 nodeId 去重（同一图片节点只贡献一次，直接上游优先）。
 * 视频上游不取封面（画布规则：任何节点不得消费视频）。
 */
export function collectUpstreamImageAssetGroups(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[]
): UpstreamImageAssetGroup[] {
  return collectImageAssetGroups(targetNodeId, nodes, edges, new Set<string>());
}

/**
 * 内部：沿上游收集图片资产分组。
 * - image / image_group：自身资产；
 * - 开启「图片透传」的 llm：递归展开其上游图片资产（保留原稳定图号）；
 * - visited 用于跨层去重与防环（同一图片节点只贡献一次，直接上游优先）。
 */
function collectImageAssetGroups(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  visited: Set<string>
): UpstreamImageAssetGroup[] {
  const target = nodes.find((n) => n.id === targetNodeId);
  const inputOrder = (target?.data as Record<string, unknown> | undefined)?.inputOrder as
    | string[]
    | undefined;
  const upstreamIds = edges
    .filter((e) => e.targetNodeId === targetNodeId)
    .map((e) => e.sourceNodeId);
  const upstream = sortUpstreamByOrder(
    nodes.filter((n) => upstreamIds.includes(n.id)),
    inputOrder
  );

  const groups: UpstreamImageAssetGroup[] = [];
  for (const node of upstream) {
    if (visited.has(node.id)) continue;
    if (node.type === 'image') {
      visited.add(node.id);
      const url = (node.data as ImageNodeData).imageUrl;
      const normalized = url ? normalizeImageUrlForApi(url) : '';
      if (normalized) {
        groups.push({
          nodeId: node.id,
          type: node.type,
          refNo: (node.data as ImageNodeData).refNo,
          urls: [normalized],
        });
      }
    } else if (node.type === 'image_group') {
      visited.add(node.id);
      const urls = ((node.data as ImageGroupNodeData).imageUrls ?? [])
        .map((u) => (u ? normalizeImageUrlForApi(u) : ''))
        .filter(Boolean);
      if (urls.length > 0) {
        groups.push({
          nodeId: node.id,
          type: node.type,
          refNo: (node.data as ImageGroupNodeData).refNo,
          urls,
        });
      }
    } else if (node.type === 'llm' && (node.data as LlmNodeData).passthroughImages) {
      // 透传：把该 LLM 自身的上游图片资产并入当前节点可引用图集
      visited.add(node.id);
      groups.push(...collectImageAssetGroups(node.id, nodes, edges, visited));
    }
  }
  return groups;
}

/**
 * 检测从 target 出发沿着已有边是否能到达 source（用于环检测）。
 * BFS 遍历，只看 sourceNodeId → targetNodeId 方向。
 */export function wouldCreateCycle(
  sourceNodeId: string,
  targetNodeId: string,
  edges: CanvasEdge[]
): boolean {
  if (sourceNodeId === targetNodeId) return true;
  const visited = new Set<string>();
  const queue: string[] = [targetNodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === sourceNodeId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    // 找到 current 节点的所有下游
    for (const edge of edges) {
      if (edge.sourceNodeId === current && !visited.has(edge.targetNodeId)) {
        queue.push(edge.targetNodeId);
      }
    }
  }
  return false;
}

// ---------------- 上游执行态：下游运行必须等上游链路执行完毕 ----------------

/**
 * 收集目标节点的全部传递上游节点 ID（沿边向上 BFS）。
 */
export function collectTransitiveUpstreamIds(
  targetNodeId: string,
  _nodes: CanvasNode[],
  edges: CanvasEdge[]
): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [];
  for (const e of edges) {
    if (e.targetNodeId === targetNodeId) queue.push(e.sourceNodeId);
  }
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const e of edges) {
      if (e.targetNodeId === id) queue.push(e.sourceNodeId);
    }
  }
  return visited;
}

/**
 * 目标节点的上游链路（含传递上游）是否有节点正在执行：
 * 处于链路执行标记（executingNodeIds）或自身 generating 标记任一即视为执行中。
 * 下游节点在此时运行会聚合到不完整的上游输出（如 LLM 流式中途），必须等待。
 */
export function hasExecutingUpstream(
  targetNodeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  executingNodeIds: string[]
): boolean {
  const upstreamIds = collectTransitiveUpstreamIds(targetNodeId, nodes, edges);
  if (upstreamIds.size === 0) return false;
  const executing = new Set(executingNodeIds);
  return nodes.some(
    (n) =>
      upstreamIds.has(n.id) &&
      (executing.has(n.id) || !!(n.data as Record<string, unknown>).generating)
  );
}

/**
 * 等待目标节点的上游链路执行完毕（500ms 轮询）。
 * 上游节点被删除视为不再执行；超过 timeoutMs 抛错（调用方负责复位自身状态）。
 */
export async function waitForUpstreamIdle(
  targetNodeId: string,
  timeoutMs = 30 * 60 * 1000
): Promise<void> {
  const start = Date.now();
  for (;;) {
    const { nodes, edges, executingNodeIds } = getBoardState();
    if (!hasExecutingUpstream(targetNodeId, nodes, edges, executingNodeIds)) return;
    if (Date.now() - start > timeoutMs) {
      throw new Error('上游节点长时间未完成，已取消本次运行，请稍后重试');
    }
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
}
